import { useAuthStore, useModalStore, useToastStore } from '@/store';
import { getTranslation } from '@/hooks/useTranslation';
import { isWorkCreditError, workCreditRequirement } from '@/lib/workCredits';
import { isWorkModeDisabledError, notifyWorkModeUnavailable } from '@/lib/workModeAvailability';
import { trackEvent } from '@/constants/utils/analytics';

/**
 * Respond to a Work launch or follow-up that the server refused.
 *
 * Work requires Plus and consumes credits. Both entitlement and credit
 * failures are conversion moments rather than generic errors, so each opens
 * the upgrade path that lets the member continue their task.
 *
 * @param {Error & { status?: Number, data?: Object }} err
 * @param {{ fallback?: String, source?: String }} [options]
 * @returns {'credits'|'plus'|'session_limit'|'unavailable'|'waiting'|'error'} which path was taken
 */
export function reportWorkLaunchError(err, { fallback, source = 'work' } = {}) {
  const reason = err?.data?.reason;
  const { openModal } = useModalStore.getState().actions;

  if (reason === 'work_submission_pending') {
    useToastStore.getState().actions.addToast(err.message, 'info');
    return 'waiting';
  }

  // Kill switch (WORK_MODE_DISABLED on the API): not a failure of the
  // member's, so it gets the "back soon" notice rather than an error toast.
  if (isWorkModeDisabledError(err)) {
    trackEvent('product', 'work_mode_unavailable', source);
    notifyWorkModeUnavailable();
    return 'unavailable';
  }

  if (reason === 'work_session_limit') {
    const { limit, activeSessions, canUpgrade, premiumLimit } = err.data;
    trackEvent('conversion', 'work_session_limit', source);
    openModal('WorkSessionLimitModal', { limit, activeSessions, canUpgrade, premiumLimit });
    return 'session_limit';
  }

  if (isWorkCreditError(err)) {
    trackEvent('conversion', 'work_out_of_credits', source);
    // Subscribers already have Plus, so the pitch is "top up, go Premium, or
    // chat with Arya free" rather than "upgrade to Plus".
    const flags = useAuthStore?.getState?.()?.user?.flags || {};
    const isPlus = !!flags.isPlus;
    openModal('OutOfCreditsModal', {
      title: isPlus
        ? getTranslation('work.outOfCredits.titlePlus')
        : getTranslation('work.outOfCredits.title'),
      subtitle: isPlus
        ? getTranslation(flags.isPlusPremium ? 'work.outOfCredits.subtitlePremium' : 'work.outOfCredits.subtitlePlus')
        : getTranslation('work.outOfCredits.subtitle'),
      source: `work_${source}`,
      workRecoveryMode: /resume|follow_up|send_now|video/.test(source) ? 'paused' : 'start',
      workCreditError: { message: err?.message, requiredCredits: workCreditRequirement(err) },
    });
    return 'credits';
  }

  if (reason === 'plus_required') {
    trackEvent('conversion', 'work_plus_required', source);
    openModal('PlusFeatureModal', { feature: 'work', source });
    return 'plus';
  }

  useToastStore.getState().actions.addToast(
    err?.message || fallback || getTranslation('work.launchFailed'),
    'error',
  );
  return 'error';
}

export default reportWorkLaunchError;
